/**
 * Tertiary Button Component
 * Text-only button for less prominent actions
 * Part of SafePassage Design System
 */

import { memo } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { designTokens } from '../../constants/designTokens';

interface TertiaryButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
}

export const TertiaryButton = memo(function TertiaryButton({
  children,
  fullWidth = false,
  size = 'medium',
  style,
  disabled,
  className = '',
  ...props
}: TertiaryButtonProps) {
  const buttonClass = `
    tertiary-button
    tertiary-button-${size}
    ${fullWidth ? 'tertiary-button-full' : ''}
    ${className}
  `.trim();

  return (
    <>
      <button
        className={buttonClass}
        style={style}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>

      <style>{`
        .tertiary-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: ${designTokens.typography.fontFamily.primary};
          font-weight: ${designTokens.typography.fontWeight.medium};
          border: none;
          border-radius: ${designTokens.borderRadius.md};
          background-color: transparent;
          color: ${designTokens.colors.text.secondary};
          text-align: center;
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
          transition: all ${designTokens.transitions.fast} ${designTokens.transitions.easing.default};
          outline: none;
          
          /* Touch device optimizations */
          -webkit-tap-highlight-color: transparent;
          user-select: none;
          touch-action: manipulation;
        }

        .tertiary-button:disabled {
          color: ${designTokens.colors.text.tertiary};
          cursor: not-allowed;
          opacity: 0.5;
        }

        .tertiary-button-small {
          padding: ${designTokens.spacing.xs} ${designTokens.spacing.sm};
          font-size: ${designTokens.typography.fontSize.xs};
        }

        .tertiary-button-medium {
          padding: ${designTokens.spacing.sm} ${designTokens.spacing.md};
          font-size: ${designTokens.typography.fontSize.sm};
        }

        .tertiary-button-large {
          padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
          font-size: ${designTokens.typography.fontSize.base};
        }

        .tertiary-button:hover:not(:disabled) {
          color: ${designTokens.colors.text.primary};
          background-color: ${designTokens.colors.withOpacity.whiteLight};
        }

        .tertiary-button:active:not(:disabled) {
          transform: scale(0.98);
        }

        .tertiary-button-full {
          width: 100%;
        }

        ${designTokens.media.mobile} {
          .tertiary-button:not(.tertiary-button-full) {
            width: ${designTokens.components.button.width.mobile};
            margin: 0 auto;
          }
        }

        ${designTokens.media.tablet} {
          .tertiary-button:not(.tertiary-button-full) {
            width: ${designTokens.components.button.width.tablet};
            margin: 0 auto;
          }
        }

        ${designTokens.media.desktop} {
          .tertiary-button:not(.tertiary-button-full) {
            width: ${designTokens.components.button.width.desktop};
            margin: 0 auto;
          }
        }

        .tertiary-button:focus-visible {
          box-shadow: 0 0 0 3px ${designTokens.colors.withOpacity.brandFocus};
        }
      `}</style>
    </>
  );
});
